import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  isAadhar,
  isAccountNum,
  isAlpha,
  isAlphaNumChars,
  isEmail,
  IsIFSCCode,
  isNum,
  isSpace,
} from "../../libs/RegexUtils";
import { useParams } from "react-router";
import { serviceGetList } from "../../services/Common.service";
import {
  serviceCheckEmployeeExists,
  serviceGetEmployeeConversionInfo,
  serviceGetEmployeeEditInfo,
  serviceGetSalaryInfoInfo,
} from "../../services/Employee.service";
import useDebounce from "../../hooks/DebounceHook";
import { serviceCreateEmployees } from "../../services/EmployeesCreate.service";
import SnackbarUtils from "../../libs/SnackbarUtils";
import historyUtils from "../../libs/history.utils";
import LogUtils from "../../libs/LogUtils";
import debounce from "lodash.debounce";
import { useSelector } from "react-redux";
import Constants from "../../config/constants";
import RolesUtils from "../../libs/Roles.utils";
import RouteName from "../../routes/Route.name";

const SALARY_KEYS = [
  "basic_salary",
  "hra",
  "education_allowance",
  "special_allowance",
  "earning_one",
  "pug",
  "helper",
  "food_coupons",
  "food_coupons_option",
  "gift_coupons",
  "lta",
  "super_annuation",
  "nps",
  "nps_option",
  "vehicle_maintenance",
  "vehicle_emi",
  "fuel",
  "vpf",
  "earning_two",
  // "gross",
  "earning_three_pli",
  "er_pf",
  "er_esi",
  "er_lwf",
  "earning_four",
  "gratuity",
  "insurance",
  "perf_bonus",
  "annual_bonus",
  "two_car_maintenance",
  "two_fuel",
  "earning_five",
  "monthly_ctc",
  "em_pf",
  "em_esi",
  "em_lwf",
  "total_deduction",
  "total_pf",
  "retention_allowance",
  "car_component",
  "incremental_gross_salary",
  "earning2_vpf",
  "deduction_vpf",
  "stability_incentive",
  "deduction_vpf_pct",
  "gross_component",
  "deputation_allowance",
  "nps_part_e",
  "em_gti",
  "em_gmc",
  "nps_pct",
  "driver_expense",
  "new_type_three_earning",
  "fixed_ctc_calculate",
  "fixed_ctc",
  "net_pay",
  "driver_salary",
  "uniform_allowance",
  "telecom_allowance",
  "gti",
  "gmc",
  "stat_bonus",
  "stability_terms",
  "retention_terms",
  "performance_terms",
  "deputation_terms",
];

const TERM_KEYS = [
  "stability_terms",
  "retention_terms",
  "performance_terms",
  "deputation_terms",
];

const BOOLEAN_KEYS = [
  "is_pug",
  "is_two_car_maintenance_manual",
  "is_two_fuel_manual",
  "is_gratuity_manual",
  "is_er_pf_manual",
  "is_er_esi_manual",
  "is_em_pf_manual",
  "is_em_esi_manual",
  "is_pug_manual",
  "is_helper",
  "is_helper_manual",
  "is_food_coupons",
  "is_food_coupons_manual",
  "is_driver_expense",
  "is_driver_expense_manual",
  "is_fuel",
  "is_fuel_manual",
  "is_perf_bonus",
  "is_gift_coupons",
  "is_lta",
  "is_super_annuation",
  "is_nps",
  "is_em_pf",
  "is_earning2_vpf",
  "is_deduction_vpf",
  "is_car_component_manual",
  "is_basic_salary_manual",
  "is_em_esi",
  "is_insurance_manual",
  "is_annual_bonus_manual",
  "is_em_lwf_manual",
  "is_em_gti_manual",
  "is_em_gmc_manual",
  "is_gmc_manual",
  "is_gti",
  "is_gti_manual",
  "is_gmc",
  "is_eligible_pf_contribution",
  "freeze_pension",
];

function SalaryCalculatorHook({ location }) {
  const initialForm = {
    designation_id: "",
    grade_id: "",
    cadre_id: "",
    location_id: "",
    basic_salary: 0,
    incremental_gross_salary: 0,
    car_component: 0,
    hra: 0,
    education_allowance: 0,
    special_allowance: 0,
    earning_one: 0,
    pug: 0,
    helper: 0,
    food_coupons: 0,
    gift_coupons: 0,
    lta: 0,
    super_annuation: 0,
    nps: 0,
    vehicle_maintenance: 0,
    vehicle_emi: 0,
    fuel: 0,
    vpf: 0,
    earning_two: 0,
    earning_three_pli: 0,
    driver_expense: 0,
    new_type_three_earning: 0,
    fixed_ctc_calculatefixed_ctc_calculate: 0,
    fixed_ctc: 0,
    net_pay: 0,
    er_pf: 0,
    er_esi: 0,
    er_lwf: 0,
    earning_four: 0,
    gratuity: 0,
    insurance: 0,
    perf_bonus: 0,
    annual_bonus: 0,
    driver_salary: 0,
    uniform_allowance: 0,
    telecom_allowance: 0,
    two_car_maintenance: 0,
    two_fuel: 0,
    earning_five: 0,
    monthly_ctc: 0,
    em_pf: 0,
    em_esi: 0,
    em_lwf: 0,
    total_deduction: 0,
    total_pf: 0,
    retention_allowance: 0,
    earning2_vpf: 0,
    deduction_vpf: 0,
    // associate: 0,
    stability_incentive: 0,
    next_review_date: "",
    previous_review_date: "",
    is_address_same: false,
    is_pug: "NO",
    is_pug_manual: "NO",
    is_two_car_maintenance_manual: "NO",
    is_two_fuel_manual: "NO",
    is_gratuity_manual: "NO",
    is_er_pf_manual: "NO",
    is_er_esi_manual: "NO",
    is_em_pf_manual: "NO",
    is_em_esi_manual: "NO",
    is_helper: "NO",
    is_helper_manual: "NO",
    is_food_coupons: "NO",
    is_food_coupons_manual: "NO",
    is_driver_expense: "NO",
    is_driver_expense_manual: "NO",
    is_fuel: "NO",
    is_fuel_manual: "NO",
    is_perf_bonus: "NO",
    is_gift_coupons: "NO",
    is_lta: "NO",
    is_super_annuation: "NO",
    is_nps: "NO",
    is_em_pf: "NO",
    is_earning2_vpf: "NO",
    is_deduction_vpf: "NO",
    is_basic_salary_manual: "NO",
    is_insurance_manual: "NO",
    is_annual_bonus_manual: "NO",
    is_em_lwf_manual: "NO",
    is_em_esi: "NO",
    deduction_vpf_pct: 0,
    gross_component: 0,
    deputation_allowance: 0,
    nps_part_e: 0,
    is_em_gti_manual: "NO",
    em_gti: 0,
    is_em_gmc_manual: "NO",
    is_gmc_manual: "NO",
    em_gmc: 0,
    nps_pct: 0,
    is_gti: "YES",
    is_gti_manual: "NO",
    is_gmc: "YES",
    gti: 0,
    gmc: 0,
    stat_bonus: 0,
    is_eligible_pf_contribution: "YES",
    freeze_pension: "NO",
    stability_terms: "NONE",
    retention_terms: "NONE",
    performance_terms: "NONE",
    deputation_terms: "NONE",
  };

  const [form, setForm] = useState({ ...initialForm });
  const [PreviewData, setPreviewData] = useState(null);
  const [errorData, setErrorData] = useState({});
  const [isDialog, setIsDialog] = useState(false);
  const [salaryInfo, setSalaryInfo] = useState([
    ...SALARY_KEYS,
    ...BOOLEAN_KEYS,
  ]);
  const { id } = useParams();
  const [defaultImg, setDefaultImg] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [remotePath, setRemotePath] = useState("");
  const codeDebouncer = useDebounce(form?.emp_code, 500);
  const ChildenRef = useRef(null);
  const candidateId = location?.state?.empId;
  const empFlag = location?.state?.isOnboard;
  const traineeId = location?.state?.traineeId;
  const retiredId = location?.state?.retiredId;
  const [showExtraFields, setShowExtraFields] = useState(false);
  const [listData, setListData] = useState({
    LOCATION_DEPARTMENTS: [],
    EMPLOYEES: [],
    DEPARTMENTS: [],
    SUB_DEPARTMENTS: [],
    JOB_ROLES: [],
    HR: [],
    DESIGNATIONS: [],
    LEVEL: [],
    GRADES: [],
    CADRES: [],
  });

  const { role } = useSelector((state) => state.auth);
  const isCorporateHR = useMemo(() => {
    return RolesUtils.canAccess([Constants.ROLES.CORPORATE_HR], role);
  }, [role]);

  useEffect(() => {
    if (form?.location_id) {
      setShowExtraFields(true);
    }
  }, [form?.location_id]);

  useEffect(() => {
    serviceGetList([
      "LOCATION_DEPARTMENTS",
      "EMPLOYEES",
      "DEPARTMENTS",
      "HR",
      "SUB_DEPARTMENTS",
      "JOB_ROLES",
      "DESIGNATIONS",
      "GRADES",
      "CADRES",
      "LEVEL",
    ]).then((res) => {
      if (!res.error) {
        setListData(res.data);
      }
    });
  }, []);

  const toggleConfirmDialog = useCallback(
    (type) => {
      setIsDialog((e) => !e);
    },
    [setIsDialog]
  );

  useEffect(() => {
    if (
      listData?.EMPLOYEES?.length > 0 &&
      (candidateId || traineeId || retiredId)
    ) {
      let req;
      if (candidateId) {
        req = serviceGetEmployeeConversionInfo({ candidate_id: candidateId });
      } else if (traineeId) {
        req = serviceGetEmployeeEditInfo({ emp_id: traineeId });
      } else if (retiredId) {
        req = serviceGetEmployeeEditInfo({ emp_id: retiredId });
      }
      req.then((res) => {
        const empData = res?.data;
        setDefaultImg(empData?.image);
        setRemotePath(empData?.remote_image_path);
        if (!candidateId) {
          const { children } = empData;
          ChildenRef?.current?.setData(children);
          const hodIndex = listData?.EMPLOYEES.findIndex(
            (val) => val.id === empData?.hod_id
          );
          if (hodIndex >= 0) {
            empData.hod_id = listData?.EMPLOYEES[hodIndex];
          }
          const pmsIndex = listData?.EMPLOYEES.findIndex(
            (val) => val.id === empData?.pms_reviewer_id
          );
          if (pmsIndex >= 0) {
            empData.pms_reviewer_id = listData?.EMPLOYEES[pmsIndex];
          }
          const jobRoleIndex = listData?.JOB_ROLES.findIndex(
            (val) => val.id === empData?.job_role_id
          );
          if (jobRoleIndex >= 0) {
            empData.job_role_id = listData?.JOB_ROLES[jobRoleIndex];
          }

          const designationIndex = listData?.DESIGNATIONS.findIndex(
            (val) => val.id === empData?.designation_id
          );
          if (designationIndex >= 0) {
            empData.designation_id = listData?.DESIGNATIONS[designationIndex];
          }
          const data = { image: "" };
          Object.keys({ ...empData }).forEach((key) => {
            if (key in initialForm && key !== "image") {
              if (key === "state") {
                data[key] = empData[key].toUpperCase();
              } else if (BOOLEAN_KEYS?.includes(key)) {
                data[key] = empData[key] ? "YES" : "NO";
              } else if (key === "is_transport_facility") {
                data[key] = empData[key] ? "availed" : "notavailed";
              } else {
                data[key] = empData[key];
              }
            }
          });
          setForm({ ...initialForm, ...data });
        } else {
          const { salary } = empData;
          salaryInfo.forEach((key) => {
            if (BOOLEAN_KEYS?.includes(key)) {
              salary[key] = salary[key] ? "YES" : "NO";
            } else {
              if (["nps_pct", "deduction_vpf_pct"]?.includes(key)) {
                salary[key] = salary[key] ? salary[key] : 0;
              } else {
                salary[key] = salary[key] ? Math.round(salary[key] / 12) : 0;
              }
            }
          });
          const designationIndex = listData?.DESIGNATIONS.findIndex(
            (val) => val.id === empData?.designation?.id
          );
          if (designationIndex >= 0) {
            empData.designation_id = listData?.DESIGNATIONS[designationIndex];
          }
          const data = { image: "" };
          Object.keys({ ...initialForm }).forEach((key) => {
            if (key in initialForm && key !== "image") {
              if (key === "location_id") {
                data[key] = empData["location"]?.id;
              } else if (key === "grade_id") {
                data[key] = empData["grade"]?.id;
              } else if (key === "department_id") {
                data[key] = empData["department"]?.id;
              } else if (key === "sub_department_id") {
                data[key] = empData["sub_department"]?.id;
              } else if (key === "cadre_id") {
                data[key] = empData["cadre"]?.id;
              } else if (key === "state") {
                data[key] = empData[key]?.toUpperCase();
              } else if (key === "current_address") {
                data[key] = empData["correspondence_address"];
              } else if (key === "previous_organisation") {
                if (empData?.employment_history?.length > 0) {
                  data[key] =
                    empData["employment_history"][0]?.organisation_name;
                }
              } else {
                data[key] = empData[key];
              }
            }
          });
          setForm({ ...initialForm, ...data, ...salary });
        }
      });
    }
  }, [candidateId, traineeId, listData, retiredId]);

  const checkSalaryInfoDebouncer = useMemo(() => {
    return debounce((e) => {
      checkForSalaryInfo(e);
    }, 1000);
  }, [listData]);

  const removeError = useCallback(
    (title) => {
      const temp = JSON.parse(JSON.stringify(errorData));
      temp[title] = false;
      setErrorData(temp);
    },
    [setErrorData, errorData]
  );

  const checkForSalaryInfo = (data) => {
    if (
      data?.grade_id &&
      data?.cadre_id &&
      data?.designation_id &&
      data?.location_id
    ) {
      const incrementalGrossSalary = parseFloat(data?.incremental_gross_salary);
      if (!isNaN(incrementalGrossSalary) && incrementalGrossSalary > 0) {
        let filteredForm = {};
        for (let key in data) {
          if (salaryInfo.includes(key)) {
            if (BOOLEAN_KEYS.includes(key)) {
              filteredForm[key] = data[key] === "YES";
            } else {
              filteredForm[key] = parseInt(data[key]);
            }
          }
        }

        let req = serviceGetSalaryInfoInfo({
          grade_id: data?.grade_id,
          cadre_id: data?.cadre_id,
          designation_id: data?.designation_id,
          location_id: data?.location_id,
          ...filteredForm,
        });

        req.then((res) => {
          const salaryData = res.data;
          setPreviewData(salaryData);
          const booleanData = {};

          for (const key in salaryData) {
            if (salaryData.hasOwnProperty(key)) {
              let value = salaryData[key];
              if (BOOLEAN_KEYS.includes(key)) {
                value = value ? "YES" : "NO";
              }
              if (key === "designation_id") {
                const match = listData?.DESIGNATIONS?.find(
                  (val) => val.id === value
                );
                if (match) {
                  booleanData[key] = match;
                }
              } else {
                booleanData[key] = value;
              }
            }
          }
          setForm({
            ...data,
            ...booleanData,
            designation_id: data?.designation_id,
          });
        });
      } else {
        console.log(" monthly_ctc is not valid or <= 0, skipping service call");
      }
    } else {
      SnackbarUtils.error("Please Select the Grade, Cadre and Designation");
    }
  };

  const changeTextData = useCallback(
    (text, fieldName) => {
      let shouldRemoveError = true;
      const t = { ...form };
      if (fieldName === "name" || fieldName === "address") {
        if (!text || (isAlphaNumChars(text) && text.toString().length <= 50)) {
          t[fieldName] = text;
        }
      } else if (fieldName === "city") {
        if (!text || (isAlpha(text) && text.toString().length <= 30)) {
          t[fieldName] = text;
        }
      } else if (fieldName === "emp_code") {
        if (!text || (!isSpace(text) && isAlphaNumChars(text))) {
          t[fieldName] = text.toUpperCase();
        }
        shouldRemoveError = false;
      } else if (fieldName === "pincode") {
        if (!text || isNum(text)) {
          t[fieldName] = text;
        }
      } else if (fieldName === "before_experience") {
        if (text >= 0) {
          t[fieldName] = text;
        }
      } else if (fieldName === "is_address_same") {
        if (text) {
          t.current_address = t?.permanent_address;
        }
        t[fieldName] = text;
      } else if (TERM_KEYS.indexOf(fieldName) >= 0) {
        t[fieldName] = text;
      } else if (SALARY_KEYS.indexOf(fieldName) >= 0) {
        if (!text || isNum(text)) {
          t[fieldName] = text;
        }
      } else if (fieldName === "is_food_coupons") {
        t[fieldName] = text;
        t.food_coupons_option = text === "YES" ? "1" : "";
      } else if (fieldName === "is_nps") {
        t[fieldName] = text;
        t.nps_option = text === "YES" ? "1" : "";
      } else if (fieldName === "grade_id") {
        t[fieldName] = text;
        t["cadre_id"] = "";
      } else {
        t[fieldName] = text;
      }
      setForm(t);
      shouldRemoveError && removeError(fieldName);

      if (
        fieldName !== "incremental_gross_salary" &&
        [
          ...salaryInfo,
          "grade_id",
          "cadre_id",
          "designation_id",
          "location_id",
        ]?.includes(fieldName)
      ) {
        checkSalaryInfoDebouncer(t);
      }
    },
    [removeError, form, setForm, checkSalaryInfoDebouncer]
  );

  const checkCodeValidation = useCallback(() => {
    if (form?.emp_code) {
      serviceCheckEmployeeExists({
        code: form?.emp_code,
        id: id ? id : null,
      }).then((res) => {
        if (!res.error) {
          const errors = JSON.parse(JSON.stringify(errorData));
          if (res.data.is_exists) {
            errors["emp_code"] = "Employee Code Exists";
            setErrorData(errors);
          } else {
            delete errors.emp_code;
            setErrorData(errors);
          }
        }
      });
    }
  }, [errorData, setErrorData, form.emp_code, id]);

  useEffect(() => {
    if (codeDebouncer) {
      checkCodeValidation();
    }
  }, [codeDebouncer]);

  const onBlurHandler = useCallback(
    (type) => {
      if (form?.[type]) {
        if (type === "incremental_gross_salary") {
          checkForSalaryInfo(form);
        } else {
          changeTextData(form?.[type], type);
        }
      }
    },
    [changeTextData, checkCodeValidation, form]
  );

  const submitToServer = useCallback(() => {
    if (!isSubmitting) {
      setIsSubmitting(true);
      const vehicleObj = {};
      const fd = new FormData();

      const keysNeedingId = [
        "hod_id",
        "pms_reviewer_id",
        "designation_id",
        "job_role_id",
        "associate",
      ];

      Object.keys(form).forEach((key) => {
        const value = form[key];
        if (keysNeedingId.includes(key) && value) {
          const idValue =
            typeof value === "object" && value !== null ? value.id : value;
          if (idValue !== undefined && idValue !== null) {
            fd.append(key, idValue);
          }
        } else if (key === "is_transport_facility") {
          fd.append("is_transport_facility", value === "availed");
        } else if (BOOLEAN_KEYS.includes(key)) {
          fd.append(key, value === "YES");
        } else if (["variant", "rc_number"].includes(key)) {
          vehicleObj[key] = value;
        } else if (value !== undefined && value !== null && value !== "") {
          fd.append(key, value);
        }
      });

      if (Object.keys(vehicleObj).length > 0) {
        fd.append("vehicle", JSON.stringify(vehicleObj));
      }

      if (remotePath?.length > 0) {
        fd.append("remote_image_path", remotePath);
      }

      fd.append("children", JSON.stringify(ChildenRef.current.getData()));
      fd.append("nominee", JSON.stringify([]));

      if (candidateId) fd.append("candidate_id", candidateId);
      if (traineeId) fd.append("trainee_id", traineeId);
      if (retiredId) fd.append("retired_emp_id", retiredId);

      serviceCreateEmployees(fd).then((res) => {
        if (!res.error) {
          historyUtils.push("/employees");
        } else {
          SnackbarUtils.error(res?.message);
        }
        setIsSubmitting(false);
      });
    }
  }, [form, isSubmitting, setIsSubmitting, candidateId, traineeId, retiredId]);

  const filteredDepartments = useMemo(() => {
    const locations = listData?.LOCATION_DEPARTMENTS;
    const index = locations?.findIndex((l) => l.id === form?.location_id);
    if (index >= 0) {
      const departments = locations[index]?.departments;
      return listData?.DEPARTMENTS?.filter(
        (val) => departments.indexOf(val.id) >= 0
      );
    }
    return [];
  }, [listData, form?.location_id]);

  const filteredSubDepartments = useMemo(() => {
    return listData?.SUB_DEPARTMENTS?.filter(
      (val) => val.department_id === form?.department_id
    );
  }, [listData, form?.department_id]);
  const filteredCadres = useMemo(() => {
    return listData?.CADRES?.filter((val) => val.grade_id === form?.grade_id);
  }, [listData, form?.grade_id]);

  const getLevelValues = useMemo(() => {
    const index = listData?.GRADES?.findIndex(
      (value) => value.id === form.grade_id
    );
    return index >= 0 ? listData.GRADES[index]?.name : " ";
  }, [listData, form?.grade_id]);
  const filteredEmployees = useMemo(() => {
    return listData?.EMPLOYEES?.filter((val) => {
      return (
        val.department_id === form?.department_id &&
        val.location_id === form?.location_id
      );
    });
  }, [form?.location_id, form?.department_id, listData]);

  const filteredAssociateJobRole = useMemo(() => {
    return listData.JOB_ROLES?.filter((val) => {
      return (
        val.department_id === form?.department_id &&
        val.location_id === form?.location_id
      );
    });
  }, [form?.location_id, form?.department_id, listData]);

  const handleProfileView = useCallback(() => {
    if (!form) return;
    const previewData = { ...form };
    const fields = [
      ["designation_id", listData?.DESIGNATIONS],
      ["cadre_id", listData?.CADRES],
      ["grade_id", listData?.GRADES],
      ["location_id", listData?.LOCATION_DEPARTMENTS],
    ];

    fields.forEach(([key, list]) => {
      const value = form[key];
      if (value && typeof value === "string") {
        const match = list?.find((item) => item.id == value);
        if (match) {
          previewData[key] = match;
        }
      }
    });

    historyUtils.push({
      pathname: RouteName.SALARY_CALCULATOR_DETAIL,
      state: { PreviewData: previewData },
    });
  }, [form, listData]);

  return {
    form,
    errorData,
    handleProfileView,
    listData,
    changeTextData,
    onBlurHandler,
    removeError,
    filteredDepartments,
    filteredSubDepartments,
    filteredEmployees,
    filteredCadres,
    getLevelValues,
    filteredAssociateJobRole,
    ChildenRef,
    empFlag,
    defaultImg,
    toggleConfirmDialog,
    isDialog,
    submitToServer,
    isCorporateHR,
    showExtraFields,
  };
}

export default SalaryCalculatorHook;
